"use client";


import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { getMarketingChrome } from "~/lib/siteglue/marketing-chrome";
import { adaptMarketingCopyForBrand } from "~/lib/siteglue/adapt-marketing-copy";

export const faqs = [
  {
    question: "How does Inbound GPT handle patient calls?",
    answer: "Inbound GPT uses AI to answer inbound patient calls 24/7, including after hours and weekends. It can schedule appointments, answer common questions about your practice, handle prescription refill requests, and seamlessly hand off complex issues to your staff — all while maintaining a natural, empathetic conversation.",
  },
  {
    question: "What is Inbound GPT used for?",
    answer: "Inbound GPT handles administrative and communication tasks for healthcare practices: answering patient calls, scheduling appointments, responding to website chat messages, capturing new patient leads, sending reminders, and managing after-hours inquiries. It does not provide medical advice, diagnoses, or clinical recommendations.",
  },
  {
    question: "Is Inbound GPT HIPAA compliant?",
    answer: "Inbound GPT is designed with HIPAA compliance in mind. Patient data is encrypted in transit and at rest. The platform provides audit trails for interactions and undergoes regular security assessments. Practices can also sign a Business Associate Agreement (BAA) to meet their compliance requirements.",
  },
  {
    question: "What practice management systems does Inbound GPT integrate with?",
    answer: "Inbound GPT integrates with 300+ EMR and practice management systems through Keragon MCP, including Open Dental, Dentrix, Juvonno, Jane App, ChiroTouch, DrChrono, and Athenahealth. It also supports Google Calendar, Zapier, and custom integrations via API.",
  },
  {
    question: "What communication channels does Inbound GPT support?",
    answer: "Inbound GPT operates across five communication channels: website chat widget, phone calls, SMS/text messaging, WhatsApp, and Facebook Messenger. All channels are managed from a single dashboard, so your team sees every patient interaction in one place.",
  },
  {
    question: "How long does it take to set up Inbound GPT?",
    answer: "Most practices are up and running within minutes. The guided setup walks you through customizing your AI's identity, connecting your practice management system, and adding your practice-specific FAQs and procedures. No technical expertise is required.",
  },
  {
    question: "What happens when Inbound GPT can't handle a request?",
    answer: "Inbound GPT recognizes when a conversation needs human attention and performs a team handoff. Your staff receives full context of the conversation — including the patient's question and all prior messages — so patients don't have to repeat themselves.",
  },
  {
    question: "Can I customize how Inbound GPT sounds and communicates?",
    answer: "Yes. You can customize your AI's name, personality traits, empathy levels, tone, and communication style. Inbound GPT also supports multiple languages with auto-translation, so it can serve diverse patient populations naturally.",
  },
  {
    question: "What healthcare specialties does Inbound GPT support?",
    answer: "Inbound GPT currently supports dental, physical therapy, chiropractic, optometry, cosmetic surgery, dermatology, internal medicine, sports medicine, and orthodontic practices. The AI is trained on workflows and vocabulary specific to each specialty.",
  },
  {
    question: "Do I need to sign a long-term contract?",
    answer:
      "No. Inbound GPT uses unit-based pricing for the channels and tools you enable — Live Chat is free, Text Messages and Email are low monthly add-ons, and Phone/EMR have clear monthly fees plus included usage. No long-term commitment. Cancel anytime. See the pricing calculator for a full estimate.",
  },
  {
    question: "How does the free trial work?",
    answer:
      "Start with free Live Chat and included free usage tiers (chat conversations, SMS, and phone/video minutes) with no credit card required. Configure your agent, test in preview, and add paid channels or tools when you are ready.",
  },
  {
    question: "Does Inbound GPT provide medical advice?",
    answer: "No. Inbound GPT is an administrative and communication tool. It handles scheduling, patient inquiries, lead capture, and front-desk tasks. It does not provide medical advice, diagnoses, treatment recommendations, or clinical decision support.",
  },
];

export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const chrome = getMarketingChrome();
  const adaptedFaqs = useMemo(
    () =>
      faqs.map((faq) => ({
        question: adaptMarketingCopyForBrand(faq.question, chrome.brandId),
        answer: adaptMarketingCopyForBrand(faq.answer, chrome.brandId),
      })),
    [chrome.brandId],
  );

  return (
    <section id="faq" data-testid="section-faq" className="relative py-24 sm:py-32">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        <div className="absolute inset-0 bg-muted/30" />
      </div>

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-3xl sm:text-4xl font-bold tracking-tight">
            Frequently Asked <span className="text-primary">Questions</span>
          </motion.h2>
          <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: 0.1 }} className="mt-4 text-lg text-muted-foreground">
            Everything you need to know about {chrome.productName}
          </motion.p>
        </div>

        <div className="space-y-3">
          {adaptedFaqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: index * 0.05 }}
              className="rounded-md border border-border bg-card overflow-hidden"
              data-testid={`faq-item-${index}`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between px-6 py-4 text-left"
                data-testid={`faq-toggle-${index}`}
              >
                <span className="font-medium text-sm pr-4">{faq.question}</span>
                <ChevronDown className={`w-4 h-4 flex-shrink-0 text-muted-foreground transition-transform duration-200 ${openIndex === index ? "rotate-180" : ""}`} />
              </button>
              <div className={`overflow-hidden transition-all duration-200 ${openIndex === index ? "max-h-96" : "max-h-0"}`}>
                <p className="px-6 pb-4 text-sm text-muted-foreground leading-relaxed">{faq.answer}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
